import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Camera, CheckCircle2, XCircle, Wifi, WifiOff, Volume2,
  Clock, AlertTriangle, Image, ImageOff
} from 'lucide-react';

const DECISION_BG = {
  GREEN: 'bg-gradient-to-br from-emerald-500 to-emerald-700',
  RED: 'bg-gradient-to-br from-red-500 to-red-700',
  NO_PLATE: 'bg-gradient-to-br from-amber-500 to-amber-700',
};

const DECISION_ICON = {
  GREEN: <CheckCircle2 className="w-24 h-24 mx-auto animate-pulse" />,
  RED: <XCircle className="w-24 h-24 mx-auto animate-pulse" />,
};

function getDecisionLabel(reading) {
  if (reading.decision === 'GREEN' && reading.notes?.includes('manual approval')) return 'ACCESS GRANTED - MANUAL APPROVAL';
  if (reading.decision === 'GREEN') return 'ACCESS GRANTED';
  if (reading.decision === 'RED') return 'ACCESS DENIED';
  return 'NO PLATE DETECTED';
}

function dotColor(decision) {
  if (decision === 'GREEN') return 'bg-emerald-500';
  if (decision === 'RED') return 'bg-red-500';
  return 'bg-amber-500';
}

export function ANPRLiveFeed({
  anprConnected,
  anprConfigured,
  latestReading,
  recentReadings,
  soundEnabled,
  setSoundEnabled,
  showSnapshot,
  setShowSnapshot,
  formatIST,
  formatISTTime,
  blacklistInfo
}) {
  const isBlacklisted = !!blacklistInfo?.is_blacklisted;
  const blPicUrl = blacklistInfo?.vehicle_id
    ? `${process.env.REACT_APP_BACKEND_URL}/api/vehicles/${blacklistInfo.vehicle_id}/picture`
    : null;
  return (
    <Card className="shadow-xl border-0" data-testid="live-anpr-card">
      <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-t-xl">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Live Gate Camera (ANPR)
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowSnapshot(!showSnapshot)}
              className="text-white hover:bg-slate-700"
              title={showSnapshot ? "Hide snapshot" : "Show snapshot"}
            >
              {showSnapshot ? <Image className="w-4 h-4" /> : <ImageOff className="w-4 h-4 opacity-50" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-slate-700"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <Volume2 className="w-4 h-4 opacity-50" />}
            </Button>
            {anprConnected ? (
              <Badge className="bg-emerald-500 text-white">
                <Wifi className="w-3 h-3 mr-1" /> Live
              </Badge>
            ) : anprConfigured ? (
              <Badge className="bg-amber-500 text-white">
                <WifiOff className="w-3 h-3 mr-1" /> Connecting...
              </Badge>
            ) : (
              <Badge variant="secondary">
                <WifiOff className="w-3 h-3 mr-1" /> Not Configured
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className={`relative h-72 flex flex-col items-center justify-center transition-all duration-500 ${
          isBlacklisted
            ? 'bg-black'
            : DECISION_BG[latestReading?.decision] || 'bg-gradient-to-br from-slate-600 to-slate-800'
        }`} data-testid="anpr-display">
          {/* Blacklist overlay with car photo background */}
          {isBlacklisted && blPicUrl && (
            <div className="absolute inset-0">
              <img
                src={blPicUrl}
                alt="Blacklisted vehicle"
                className="w-full h-full object-cover opacity-30 grayscale"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          )}
          {showSnapshot && !isBlacklisted && latestReading?.snapshot_url ? (
            <div className="absolute inset-0">
              <img
                src={latestReading.snapshot_url}
                alt="Gate Camera Snapshot"
                className="w-full h-full object-cover opacity-40"
              />
            </div>
          ) : null}

          <div className="relative z-10 text-center text-white">
            {isBlacklisted ? (
              <div data-testid="anpr-blacklist-overlay">
                <div className="mb-3"><AlertTriangle className="w-24 h-24 mx-auto text-red-400 animate-pulse" /></div>
                <div className="inline-block bg-red-600 text-white text-2xl font-extrabold tracking-widest px-4 py-1 rounded mb-2 shadow-lg" data-testid="anpr-blacklist-badge">⛔ BLACKLISTED</div>
                <div className="text-3xl font-mono font-bold tracking-wider mb-1 drop-shadow-lg">{latestReading?.plate || blacklistInfo.plate_number}</div>
                {blacklistInfo.owner_name && <div className="text-sm opacity-90">{blacklistInfo.owner_name} · Villa {blacklistInfo.villa_number}</div>}
                {blacklistInfo.reason && <div className="text-sm bg-red-700/70 inline-block rounded px-2 py-0.5 mt-1">Reason: {blacklistInfo.reason}</div>}
                <div className="text-xs opacity-70 mt-2">Do not allow entry — escalate to management.</div>
              </div>
            ) : latestReading ? (
              <>
                <div className="mb-4">
                  {DECISION_ICON[latestReading.decision] || <AlertTriangle className="w-24 h-24 mx-auto animate-pulse" />}
                </div>
                {/* Visitor name + villa take top billing when present; the
                    actual plate (often empty for walk-in visitors) is shown
                    smaller underneath. For raw ANPR reads (no visitor name),
                    the plate stays prominent like before. For GREEN resident
                    reads with a matched vehicle, we show Villa + owner as a
                    subtitle under the plate. */}
                {latestReading.visitor_name ? (
                  <>
                    <div
                      className="text-3xl font-bold tracking-wide mb-1 drop-shadow-lg"
                      data-testid="anpr-visitor-name"
                    >
                      {latestReading.visitor_name}
                    </div>
                    {latestReading.villa_number && (
                      <div
                        className="text-lg font-mono opacity-95 mb-2"
                        data-testid="anpr-visitor-villa"
                      >
                        Villa {latestReading.villa_number}
                      </div>
                    )}
                    {latestReading.plate &&
                      !latestReading.plate.startsWith('VISITOR-') &&
                      !latestReading.plate.startsWith('RESIDENT-APPROVED') && (
                        <div className="text-sm font-mono opacity-80 mb-2">
                          Plate: {latestReading.plate}
                        </div>
                      )}
                  </>
                ) : (
                  <>
                    <div className="text-4xl font-mono font-bold tracking-wider mb-2 drop-shadow-lg">
                      {latestReading.plate || 'NO PLATE'}
                    </div>
                    {/* Resident vehicle match (GREEN) — surface the villa
                        so the guard sees CONTEXT, not just a plate string.
                        Owner name is intentionally NOT shown for privacy. */}
                    {latestReading.decision === 'GREEN' && latestReading.villa_number && (
                      <div
                        className="text-xl font-semibold mb-2 drop-shadow"
                        data-testid="anpr-resident-villa"
                      >
                        Villa {latestReading.villa_number}
                      </div>
                    )}
                  </>
                )}
                <div className="text-2xl font-bold mb-2">
                  {getDecisionLabel(latestReading)}
                </div>
                {latestReading.notes?.includes('manual approval') && (
                  <div className="text-sm opacity-90 mb-2 bg-white/20 rounded-lg px-3 py-1">
                    {latestReading.notes}
                  </div>
                )}
                <div className="flex items-center justify-center gap-4 text-sm opacity-80">
                  <span>Confidence: {latestReading.confidence}%</span>
                  <span>-</span>
                  <span>{latestReading.camera}</span>
                </div>
                <div className="flex items-center justify-center gap-1 mt-2 text-xs opacity-70">
                  <Clock className="w-3 h-3" />
                  {formatIST(latestReading.timestamp2 || latestReading.timestamp)}
                </div>
              </>
            ) : (
              <div className="text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-xl font-semibold">Waiting for vehicle...</p>
                <p className="text-sm opacity-70 mt-2">
                  {anprConfigured ? 'Camera feed active' : 'ANPR not configured'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 bg-slate-50 border-t max-h-48 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-600 mb-2">Recent Readings</p>
          {recentReadings.length === 0 ? (
            <p className="text-sm text-slate-400">No recent readings</p>
          ) : (
            <div className="space-y-1">
              {recentReadings.slice(0, 5).map((reading, idx) => {
                // Synthetic plates (gate pass / resident approval) are useless
                // to display directly. Prefer visitor_name + villa when present.
                const isSyntheticPlate = (reading.plate || '').startsWith('VISITOR-')
                  || (reading.plate || '').startsWith('RESIDENT-APPROVED');
                const primary = (isSyntheticPlate && reading.visitor_name)
                  ? reading.visitor_name
                  : (reading.plate || 'NO PLATE');
                return (
                <div key={reading.id || idx} className="flex items-start justify-between text-sm py-1.5 border-b border-slate-200 last:border-0">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dotColor(reading.decision)}`}></div>
                    <div className="min-w-0">
                      <span className="font-mono font-semibold text-sm block truncate" data-testid="reading-plate">
                        {primary}
                      </span>
                      {(reading.visitor_name || reading.villa_number) && !(isSyntheticPlate && primary === reading.visitor_name && !reading.villa_number) && (
                        <span className="text-[11px] text-slate-500 block truncate" data-testid="reading-visitor-villa">
                          {!isSyntheticPlate && reading.visitor_name ? reading.visitor_name : ''}
                          {!isSyntheticPlate && reading.visitor_name && reading.villa_number ? ' · ' : ''}
                          {reading.villa_number ? `Villa ${reading.villa_number}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 flex-shrink-0 pl-2">
                    {formatISTTime(reading.timestamp2 || reading.timestamp)}
                  </span>
                </div>
              );})}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
